import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Campaigns() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ name: "", niche: "", industry: "", location: "", role_titles: "", company_size: "", description: "", count: "10" });

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const load = async () => {
    const r = await api.get("/campaigns");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.niche) { toast.error("Name and niche are required"); return; }
    setGenerating(true);
    try {
      await api.post("/campaigns", { ...form, count: parseInt(form.count, 10) });
      toast.success("Campaign live — prospects ready");
      setOpen(false);
      setForm({ name: "", niche: "", industry: "", location: "", role_titles: "", company_size: "", description: "", count: "10" });
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create campaign");
    } finally { setGenerating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign and all its prospects?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Deleted");
    load(); refresh();
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Campaigns</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Prospect campaigns</h1>
          <p className="text-[#5C5C66] mt-2 max-w-2xl">Define your ICP. We surface scored prospects in seconds.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white font-medium shadow-soft" data-testid="new-campaign-btn">
              <Plus className="h-4 w-4 mr-2" /> New campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tighter">New campaign</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Campaign name *">
                  <Input value={form.name} onChange={set("name")} placeholder="DTC skincare founders Q1" className="rounded-lg" data-testid="campaign-name" />
                </Field>
                <Field label="How many prospects?">
                  <Select value={form.count} onValueChange={(v) => setForm({ ...form, count: v })}>
                    <SelectTrigger className="rounded-lg" data-testid="campaign-count"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["5", "10", "15", "20", "25"].map((n) => <SelectItem key={n} value={n}>{n} prospects</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Niche / keyword *">
                  <Input value={form.niche} onChange={set("niche")} placeholder="DTC skincare, B2B SaaS founders" className="rounded-lg" data-testid="campaign-niche" />
                </Field>
                <Field label="Industry">
                  <Input value={form.industry} onChange={set("industry")} placeholder="Beauty & Personal Care" className="rounded-lg" data-testid="campaign-industry" />
                </Field>
                <Field label="Location">
                  <Input value={form.location} onChange={set("location")} placeholder="United States, EU" className="rounded-lg" data-testid="campaign-location" />
                </Field>
                <Field label="Target role titles">
                  <Input value={form.role_titles} onChange={set("role_titles")} placeholder="Founder, CEO, VP Growth" className="rounded-lg" data-testid="campaign-roles" />
                </Field>
                <Field label="Company size">
                  <Input value={form.company_size} onChange={set("company_size")} placeholder="11-50, 51-200" className="rounded-lg" data-testid="campaign-size" />
                </Field>
              </div>
              <Field label="Additional context">
                <Textarea value={form.description} onChange={set("description")} placeholder="Recently raised seed/A. Selling on Shopify. Eco-conscious." className="rounded-lg min-h-[80px]" data-testid="campaign-description" />
              </Field>
              <DialogFooter>
                <Button type="submit" disabled={generating} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white" data-testid="campaign-submit">
                  {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Surfacing prospects…</>) : "Surface prospects"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#EAEAE3] rounded-2xl p-16 text-center">
          <div className="font-display text-2xl font-bold mb-2">No campaigns yet</div>
          <div className="text-[#5C5C66] mb-6">Create one to surface your first scored prospects.</div>
          <Button onClick={() => setOpen(true)} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
            <Plus className="h-4 w-4 mr-2" /> New campaign
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-[#EAEAE3] rounded-2xl p-5 hover:shadow-soft-md hover:-translate-y-0.5 transition-all group">
              <div className="flex items-start justify-between mb-3">
                <div className="label-mono">{new Date(c.created_at).toLocaleDateString()}</div>
                <button onClick={() => remove(c.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#9A9AA3] hover:text-red-500" data-testid={`delete-campaign-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <Link to={`/app/campaigns/${c.id}`} className="block" data-testid={`open-campaign-${c.id}`}>
                <div className="font-display text-xl font-bold tracking-tighter mb-1 truncate">{c.name}</div>
                <div className="text-sm text-[#5C5C66] mb-4 truncate">{c.niche}{c.location ? ` · ${c.location}` : ""}</div>
                <div className="flex items-center justify-between border-t border-[#EAEAE3] pt-3">
                  <span className="label-mono">Prospects</span>
                  <span className="font-mono font-bold text-2xl text-[#002FA7]">{c.leads_count}</span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-mono">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
